<div class="inner-mat-dialog flex justify-between content-stretch items-stretch flex-col">
  <div>
    <h1 mat-dialog-title tabindex="0" class="xs:flex-1 sm:basis-full">
      Onboard Customer VM
    </h1>
  </div>

  <mat-dialog-content class="h-full">

    <!-- Feature-flagged: multi-step flow with site readiness check -->
    <ng-container *ngIf="sitePreparationEnabled; else legacyContent">
      <mat-horizontal-stepper #stepper linear labelPosition="bottom" (selectionChange)="onStepChange($event)">

        <!-- Step 1: Site Readiness -->
        <mat-step [completed]="!!sitePreparationCmd" label="Site Readiness">
          <div class="flex flex-col gap-4 py-4">
            <span>Run the following command on the target Linux device to verify that the environment is ready for Stratus installation.</span>

            <div *ngIf="sitePreparationRequestInProgress" class="flex flex-col mt-4 items-center">
              <mat-spinner [diameter]="40"></mat-spinner>
            </div>

            <div *ngIf="sitePreparationCmd" class="flex flex-col gap-4">
              <div class="bg-gray-200 rounded">
                <div
                  class="flex justify-between items-center w-full px-4 py-2 box-border border-solid border-gray-600 border-0 border-b">
                  <label class="font-bold">Site Readiness Command</label>
                  <div class="flex gap-4 items-center">
                    <label for="copy-readiness-btn">Copy</label>
                    <button id="copy-readiness-btn" mat-mini-fab class="!bg-transparent !shadow-none hover:!bg-gray-300"
                      [cdkCopyToClipboard]="sitePreparationCmd">
                      <mat-icon>content_copy</mat-icon>
                    </button>
                  </div>
                </div>
                <div class="w-full h-full bg-gray-100 px-4 py-2 box-border">
                  <pre><code class="whitespace-pre-wrap">{{sitePreparationCmd}}</code></pre>
                </div>
              </div>

              <span>Once the script completes successfully, click <b>Next</b> to proceed with installation.</span>
            </div>
          </div>
        </mat-step>

        <!-- Step 2: Install Stratus -->
        <mat-step label="Install Stratus">
          <div class="flex flex-col gap-4 py-4">
            <ng-container *ngTemplateOutlet="installForm"></ng-container>
          </div>
        </mat-step>

      </mat-horizontal-stepper>
    </ng-container>

    <!-- Original single-step flow (flag off) -->
    <ng-template #legacyContent>
      <div class="flex flex-col gap-4">
        <ng-container *ngTemplateOutlet="installForm"></ng-container>
      </div>
    </ng-template>

    <!-- Shared install form template used by both paths -->
    <ng-template #installForm>
      <span>Fill out this form and click Generate Command to create a temporary, one-line installation command.</span>
      <form [formGroup]="onboardVmForm">
        <div class="xs:flex xs:flex-col flex gap-4 flex-row h-20">
          <app-organization-select class="flex-1 flex-shrink-0" [form]="onboardVmForm"
            [orgControlName]="'organizationId'"></app-organization-select>
          <mat-form-field floatLabel="never" class="xs:flex-1 gt-xs:basis-1/4">
            <label>
              <app-icon icon="asterisk" aria-hidden="true" size="10"></app-icon>
              Device Name
              <custom-tooltip text="A name to uniquely identify this device for the customer"></custom-tooltip>
            </label>
            <input type="text" matInput appNoHtmlTags trim [formControlName]="'deviceId'">
            <mat-error *ngIf="onboardVmForm.get('deviceId')?.hasError('required')">
              Device Name required.
            </mat-error>
            <mat-error *ngIf="onboardVmForm.get('deviceId')?.hasError('pattern')">
              Only letters, numbers, spaces, and : _ . - are allowed and must end with a letter, number, or -
            </mat-error>
          </mat-form-field>
          <div class="flex ml-auto">
            <button mat-button class="self-center justify-self-end btn-default btn-md mt-3"
              [disabled]="onboardVmForm.invalid || isCombinedLengthExceeded()" (click)="getCommand()">Generate
              Command</button>
          </div>
        </div>
        <div *ngIf="isCombinedLengthExceeded()" class="mt-3 text-xs self-center mat-mdc-form-field-error">
            Account and Device Name together cannot exceed 60 characters.
        </div>
        <div *ngIf="canShowPhysicalDevice" class="xs:flex xs:flex-col flex gap-4 flex-row pb-4 border-solid border-blue-900 border-0 border-b">
          <mat-checkbox class="self-center" [formControlName]="'physicalDevice'">
            <label for="mat-checkbox-2-input">
                Physical Device
                <custom-tooltip text="ASUS PN42 or similar device. ONLY FOR USE BY PARATA/BD MANUFACTURING"></custom-tooltip>
            </label>
          </mat-checkbox>
        </div>
      </form>

      <div *ngIf="requestInProgress" class="flex flex-col mt-4 items-center">
        <mat-spinner [diameter]="40"></mat-spinner>
      </div>

      <div *ngIf="cmd" class="flex flex-col gap-4">
        <span>The following command will be valid for the next ten minutes.</span>

        <div class="bg-gray-200 rounded">
          <div
            class="flex justify-between items-center w-full px-4 py-2 box-border border-solid border-gray-600 border-0 border-b">
            <label class="font-bold">Installation Command</label>
            <div class="flex gap-4 items-center">
              <label for="submit-btn">Copy</label>
              <button id="submit-btn" mat-mini-fab class="!bg-transparent !shadow-none hover:!bg-gray-300"
                [cdkCopyToClipboard]="cmd">
                <mat-icon>content_copy</mat-icon>
              </button>
            </div>
          </div>
          <div class="w-full h-full bg-gray-100 px-4 py-2 box-border">
            <pre><code class="whitespace-pre-wrap">{{cmd}}</code></pre>
          </div>
        </div>

        <span *ngIf="hubDeviceId">After a successful installation, refreshing the pre-provisioned list will show a new device called
          <b>{{hubDeviceId}}.</b></span>
      </div>
    </ng-template>
  </mat-dialog-content>

  <mat-dialog-actions class="flex justify-end content-end items-end flex-row gap-2">
    <ng-container *ngIf="sitePreparationEnabled">
      <button *ngIf="currentStepIndex > 0" mat-button class="btn-default btn-md" (click)="prevStep()">Back</button>
      <button *ngIf="currentStepIndex === 0" mat-button class="btn-default btn-md"
        [disabled]="!sitePreparationCmd" (click)="nextStep()">Next</button>
    </ng-container>
    <button mat-button class="btn-default btn-md" [mat-dialog-close]="true" tabindex="0" type="button">Close</button>
  </mat-dialog-actions>
</div>